import { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { CollapseButton, CollapseContent, CollapsePanel } from '@g17eco/molecules/collapse-panel';
import { CHART_SUBTYPE, ChartEditorProps, QuestionsMapUtr } from '../types';
import { ChartData, isDisaggregatedRendering, transformVariables } from '@routes/custom-dashboard/utils';
import { ChartTypeSwitcher } from '../chart-type-switcher';
import { Button, Input } from 'reactstrap';
import {
  ChartSubType,
  DashboardSourceOption,
  GridDashboardItemCreate,
  InsightDashboardItemChartType,
  InsightDashboardItemType,
} from '@g17eco/types/insight-custom-dashboard';
import { MetricsSelector } from '../metric-selector';
import { useGetInitiativeBlueprintQuestionsQuery } from '@api/admin-dashboard';
import { QueryError } from '@g17eco/molecules/query/QueryError';
import { Loader } from '@g17eco/atoms/loader';
import { UtrVariable } from '@routes/summary/insights/utils/constants';
import {
  areBreakoutColumnsKnown,
  doSupportMultiple,
  getUniqueUtrVariables,
  isBreakoutCapableSubType,
  isMultipleMetricsChart,
  checkIsCustomTitle,
  getChartCalculation,
  getChartTitleByType,
  isValidUtrVariable,
} from '../utils/dashboard-utils';
import { EditorTemplate } from './EditorTemplate';
import { CustomDashboardContext } from '@routes/custom-dashboard/context/CustomDashboardWrapper';
import { getChartData } from '../utils/chart-utils';
import { INITIATIVE, QUESTION, SURVEY } from '@constants/terminology';
import { SelectFactory, SelectTypes } from '@g17eco/molecules/select/SelectFactory';
import { SimpleTooltip } from '@g17eco/molecules/simple-tooltip';
import { MAX_LENGTH_TITLE } from './constant';
import { TableColumnType } from '@g17eco/types/universalTracker';
import { isTableGroupAggregation } from '@utils/universalTracker';
import { BreakoutConfig } from './BreakoutConfig';
import { handleRouteError } from '../../../logger';
import { BasicAlert } from '@g17eco/molecules/alert';

const TITLE_TOOLTIP = 'If left blank, title will default to data-point selected.';

type BuildSubmitItemParams = {
  editingItem: ChartEditorProps['editingItem'];
  type: InsightDashboardItemChartType;
  subType: ChartSubType;
  title: string | undefined;
  variables: ReturnType<typeof transformVariables>;
  calculation: ReturnType<typeof getChartCalculation> | undefined;
  icon: string | undefined;
  breakout: ChartData['breakout'];
};

const buildSubmitItem = ({
  editingItem,
  type,
  subType,
  title,
  variables,
  calculation,
  icon,
  breakout,
}: BuildSubmitItemParams): GridDashboardItemCreate => {
  // Why: ChartItem union members don't all declare breakout — the cast gives a uniform
  // optional shape so we can destructure it out for the SDG/Integration branches.
  const { breakout: _breakout, ...editingItemWithoutBreakout } = editingItem as { breakout?: unknown } & typeof editingItem;
  const base = { ...editingItemWithoutBreakout, subType, title, variables, icon };
  if (type === InsightDashboardItemType.Chart) {
    return { ...base, type, calculation: breakout ? undefined : calculation, breakout };
  }
  // Branches kept separate so `type` narrows to one literal for the
  // GridDashboardItemCreate union — bodies are otherwise identical.
  if (type === InsightDashboardItemType.SDGTracker) {
    return { ...base, type, calculation };
  }
  return { ...base, type, calculation };
};

// `cleared` carries the metric code so the suppression is per-metric: swap to a
// different metric and the auto-emit defaults kick in again for that metric.
type BreakoutEditorState =
  | { kind: 'active' }
  | { kind: 'cleared'; metricCode: string };

const getTitle = ({
  chartData: { type, metrics },
  questionsMap,
}: {
  chartData: ChartData;
  questionsMap: Map<string, QuestionsMapUtr>;
}) => {
  if (!metrics || metrics.length === 0 || !type) {
    return '';
  }

  // Extract title from the first metric
  const [metric] = metrics;

  return getChartTitleByType({ chartType: type, utrVariable: metric, questionsMap });
};

export const ChartEditor = (props: ChartEditorProps) => {
  const { editingItem, handleCancel, updateItem, initiativeId, queryParams } = props;
  const [chartData, setChartData] = useState(() => getChartData(editingItem));
  // Seed `cleared` for a saved widget with metric-but-no-breakout so BreakoutConfig's
  // auto-emit doesn't fabricate a phantom default.
  const [breakoutState, setBreakoutState] = useState<BreakoutEditorState>(() => {
    const initial = getChartData(editingItem);
    const initialMetricCode = initial.metrics[0]?.code;
    return initial.breakout === undefined && initialMetricCode !== undefined
      ? { kind: 'cleared', metricCode: initialMetricCode }
      : { kind: 'active' };
  });
  const isDisaggregated = isDisaggregatedRendering({ initiativeIds: queryParams.initiativeIds });

  const { userIsStaff, integrationProviders } = useContext(CustomDashboardContext);

  const integrationCount = integrationProviders?.length;
  const chartTypeOptions = useMemo(() => {
    const options: DashboardSourceOption[] = [
      { value: InsightDashboardItemType.Chart, label: `${SURVEY.CAPITALIZED_SINGULAR} ${QUESTION.PLURAL}` },
      { value: InsightDashboardItemType.SDGTracker, label: 'SDG Contribution' },
    ];
    if (userIsStaff && integrationCount) {
      options.push({ value: InsightDashboardItemType.Integration, label: 'Integration' });
    }
    return options;
  }, [userIsStaff, integrationCount]);

  const [sourceMetrics, setSourceMetrics] = useState(() => {
    return chartTypeOptions.reduce((acc, option) => {
      return { ...acc, [option.value]: chartData.type === option.value ? chartData.metrics : [] };
    }, {} as Record<InsightDashboardItemChartType, UtrVariable[] | undefined>);
  });

  const {
    data: blueprintQuestions,
    isFetching: blueprintFetching,
    isError,
    error,
  } = useGetInitiativeBlueprintQuestionsQuery({ initiativeId });

  const isDisabled = !chartData.metrics?.length;

  const questionsMap = useMemo(() => {
    const map = new Map<string, QuestionsMapUtr>();
    if (!blueprintQuestions?.length) {
      return map;
    }

    blueprintQuestions.forEach((question) => {
      map.set(question.code, question);
    });

    integrationProviders?.forEach(provider => {
      provider.questions.forEach(question => {
        map.set(question.code, question);
      });
    });

    return map;
  }, [blueprintQuestions, integrationProviders]);

  const isCustomTitle = checkIsCustomTitle({ item: editingItem, chartData, questionsMap });

  const firstMetricCode = chartData.metrics[0]?.code;
  const selectedMetric = blueprintQuestions?.find((q) => q.code === firstMetricCode);
  const isGroupedTableMetric = selectedMetric !== undefined && isTableGroupAggregation(selectedMetric);
  // Drives state-shape effects AND BreakoutConfig visibility. Shown in disaggregated
  // view too — the inline warning surfaces the placeholder consequence.
  const isBreakoutSupportedByMetric =
    chartData.type === InsightDashboardItemType.Chart &&
    isBreakoutCapableSubType(chartData.subType) &&
    isGroupedTableMetric;
  const isMultiple = isMultipleMetricsChart(chartData) && !isBreakoutSupportedByMetric;

  // Gate the clearing effect on blueprint fetch — until it resolves selectedMetric is
  // undefined for valid metrics and would trigger a spurious clear.
  const metricResolved = !blueprintFetching;

  // Dedupe the stale-metric log so unrelated field edits don't spam Sentry.
  const loggedStaleMetricRef = useRef<string | undefined>(undefined);
  // Dedup key: `${metricCode}|${subType}` — see clear-effect below.
  const loggedClearedBreakoutRef = useRef<string | undefined>(undefined);
  useEffect(() => {
    if (!metricResolved) {
      return;
    }
    // SDG/Integration codes live outside the blueprint catalogue; their selectedMetric is legitimately undefined.
    if (chartData.type !== InsightDashboardItemType.Chart) {
      return;
    }
    // Saved metric not in blueprint: preserve the breakout (we can't tell deletion
    // from permission/partial-fetch) and surface to Sentry once per code.
    if (firstMetricCode !== undefined && selectedMetric === undefined) {
      if (loggedStaleMetricRef.current !== firstMetricCode) {
        handleRouteError(
          new Error(
            `ChartEditor: saved metric code "${firstMetricCode}" not found in initiative blueprint (deleted, permission-filtered, or partial fetch)`,
          ),
          { context: 'ChartEditor.staleMetricBlueprint', metricCode: firstMetricCode },
        );
        loggedStaleMetricRef.current = firstMetricCode;
      }
      return;
    }
    if (isBreakoutSupportedByMetric) {
      return;
    }
    // Log inside the updater so it fires only on an actual clear, not on no-op re-renders.
    setChartData((prev) => {
      if (prev.breakout === undefined) {
        return prev;
      }
      const clearKey = `${firstMetricCode ?? ''}|${chartData.subType}`;
      if (loggedClearedBreakoutRef.current !== clearKey) {
        handleRouteError(
          new Error(
            `ChartEditor: cleared persisted breakout because metric "${firstMetricCode ?? '<none>'}" + subType "${chartData.subType}" no longer supports it`,
          ),
          {
            context: 'ChartEditor.breakoutCleared',
            metricCode: firstMetricCode,
            subType: chartData.subType,
          },
        );
        loggedClearedBreakoutRef.current = clearKey;
      }
      return { ...prev, breakout: undefined };
    });
  }, [metricResolved, isBreakoutSupportedByMetric, firstMetricCode, selectedMetric, chartData.type, chartData.subType, chartData.breakout]);

  const prevMetricCodeRef = useRef(firstMetricCode);
  useEffect(() => {
    if (prevMetricCodeRef.current === firstMetricCode) {
      return;
    }
    const previousCode = prevMetricCodeRef.current;
    prevMetricCodeRef.current = firstMetricCode;
    // First-commit (undefined → defined) isn't a swap; clobbering here would clear
    // the breakout that BreakoutConfig's auto-emit just populated.
    if (previousCode === undefined) {
      return;
    }
    // Mirror the stale-blueprint gate above: preserve the breakout on a swap to a
    // metric the blueprint hasn't surfaced (likely permission/partial-fetch).
    if (firstMetricCode !== undefined && selectedMetric === undefined) {
      return;
    }
    setChartData((prev) => {
      if (prev.breakout === undefined) {
        return prev;
      }
      // Only clear when the existing breakout columns are no longer valid for the new
      // metric. If they ARE still valid, BreakoutConfig's auto-emit may have just
      // re-emitted them for the new metric (when the swap follows an X-clear); clearing
      // here would race with that emit and re-blank the breakout.
      const newTextColumnCodes = (selectedMetric?.valueValidation?.table?.columns ?? [])
        .filter((col) => col.type === TableColumnType.Text)
        .map((col) => col.code);
      if (areBreakoutColumnsKnown(prev.breakout, newTextColumnCodes)) {
        return prev;
      }
      handleRouteError(
        new Error(
          `ChartEditor: cleared persisted breakout on metric swap "${previousCode}" → "${firstMetricCode ?? '<none>'}"`,
        ),
        {
          context: 'ChartEditor.breakoutClearedOnMetricSwap',
          previousMetricCode: previousCode,
          newMetricCode: firstMetricCode,
        },
      );
      return { ...prev, breakout: undefined };
    });
    // selectedMetric is read inside to gate the stale-blueprint case (mirrors the
    // clearing effect above); the effect itself still keys off firstMetricCode changes.
    // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, [firstMetricCode]);

  // On breakout activation, trim metrics to one (MetricsSelector is single-mode). Log
  // when the trim actually drops user-added metrics so the silent data loss is traceable.
  const loggedMetricsTruncatedRef = useRef<string | undefined>(undefined);
  useEffect(() => {
    if (!isBreakoutSupportedByMetric) {
      return;
    }
    setChartData((prev) => {
      if (prev.metrics.length <= 1) {
        return prev;
      }
      const droppedCodes = prev.metrics.slice(1).map((m) => m.code ?? '').join(',');
      const truncateKey = `${firstMetricCode ?? ''}|${droppedCodes}`;
      if (loggedMetricsTruncatedRef.current !== truncateKey) {
        handleRouteError(
          new Error(
            `ChartEditor: trimmed ${prev.metrics.length - 1} additional metric(s) on breakout activation (kept "${firstMetricCode ?? '<none>'}", dropped "${droppedCodes}")`,
          ),
          {
            context: 'ChartEditor.metricsTruncatedOnBreakoutActivation',
            keptMetricCode: firstMetricCode,
            droppedCodes,
            droppedCount: prev.metrics.length - 1,
          },
        );
        loggedMetricsTruncatedRef.current = truncateKey;
      }
      return { ...prev, metrics: prev.metrics.slice(0, 1) };
    });
    // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, [isBreakoutSupportedByMetric]);

  const changeChartData = (newChartData: Partial<ChartData>) => {
    setChartData((chartData) => ({ ...chartData, ...newChartData }));
  };

  const getCachedMetrics = (type: ChartData['type'], isMultiple: boolean) => {
    const typeMetrics = type ? sourceMetrics[type] ?? [] : [];
    return isMultiple ? typeMetrics : typeMetrics.slice(0, 1);
  };

  const onSubTypeChange = (subType: ChartSubType) => {
    const nextBreakoutSupported = isBreakoutCapableSubType(subType) && isGroupedTableMetric;
    const allowMultiple = doSupportMultiple(subType) && !nextBreakoutSupported;
    changeChartData({ subType, metrics: getCachedMetrics(chartData.type, allowMultiple) });
  };

  const onSourceChange = (type: InsightDashboardItemChartType) => {
    // Load previously stored result of metrics if available.
    changeChartData({ type, metrics: getCachedMetrics(type, isMultiple) });
  };

  const onChangeChartData = (newChartData: Partial<ChartData>) => {
    const type = newChartData.type;
    if (type && newChartData.metrics) {
      // Cache results for the type
      setSourceMetrics((sourceMetrics) => ({ ...sourceMetrics, [type]: newChartData.metrics }));
    }
    changeChartData(newChartData);
  };

  const handleCreate = async () => {
    if (!chartData.metrics || !chartData.subType || !chartData.type) {
      return;
    }
    const uniqueVariables = getUniqueUtrVariables(chartData.metrics);
    const hasCustomTitle = isCustomTitle && chartData.title && !!chartData.title.trim();
    const type = chartData.type;

    if (isMultiple || isDisaggregated) {
      // If not set, use the title of the first metric
      const title = hasCustomTitle ? chartData.title : getTitle({ chartData, questionsMap });
      const variables = transformVariables(uniqueVariables);

      const calculation = getChartCalculation({
        metrics: chartData.metrics,
        variables,
        subType: chartData.subType,
        questionsMap,
        chartType: type,
      });

      const submitItem = buildSubmitItem({
        editingItem,
        type,
        subType: chartData.subType,
        title,
        variables,
        calculation,
        icon: undefined,
        breakout: chartData.breakout,
      });
      return updateItem(submitItem);
    }

    const questionData = uniqueVariables[0];
    if (!isValidUtrVariable(questionData)) {
      return;
    }

    const variables = transformVariables([questionData]);
    const title = hasCustomTitle
      ? chartData.title
      : getChartTitleByType({ chartType: type, utrVariable: questionData, questionsMap });

    const { subType } = chartData;

    const submitItem = buildSubmitItem({
      editingItem,
      type,
      subType,
      title,
      variables,
      // unset calculation as single utr chart must not have calculation otherwise it will render multi utrs chart instead
      calculation: undefined,
      icon: '',
      breakout: chartData.breakout,
    });

    return updateItem(submitItem);
  };

  return (
    <EditorTemplate isDisabled={isDisabled} handleCancel={handleCancel} handleSubmit={handleCreate}>
      {isError ? <QueryError error={error} type={'danger'} /> : null}
      {blueprintFetching ? <Loader /> : null}
      <CollapsePanel className='comment-group comments py-3' collapsed={false}>
        <CollapseButton>
          <>{CHART_SUBTYPE[chartData.subType as keyof typeof CHART_SUBTYPE]}</>
        </CollapseButton>
        <CollapseContent>
          <ChartTypeSwitcher chartSubType={chartData.subType} onSubTypeChange={onSubTypeChange} />
          <p className='mt-3 text-medium'>
            Title (optional)
            <SimpleTooltip text={TITLE_TOOLTIP} className='text-ThemeIconSecondary'>
              <i className='fal fa-info-circle ml-2' />
            </SimpleTooltip>
          </p>
          <Input
            type='text'
            className='text-md'
            maxLength={MAX_LENGTH_TITLE}
            value={chartData.title ?? ''}
            onChange={(e) => changeChartData({ title: e.target.value })}
            placeholder='Enter chart title'
          />
          <p className='mt-3 text-medium'>Source</p>
          <SelectFactory
            selectType={SelectTypes.SingleSelect}
            placeholder={'Select chart source'}
            options={chartTypeOptions}
            value={chartTypeOptions.find((option) => option.value === chartData.type) ?? null}
            onChange={(option) => (option?.value ? onSourceChange(option.value) : undefined)}
          />
          <MetricsSelector
            {...props}
            chartData={chartData}
            changeChartData={onChangeChartData}
            questionsMap={questionsMap}
            forceSingleMetric={isBreakoutSupportedByMetric}
          />
          {isBreakoutSupportedByMetric && selectedMetric ? (
            breakoutState.kind === 'cleared' && breakoutState.metricCode === firstMetricCode ? (
              <Button color='link' className='mt-3 p-0' onClick={() => setBreakoutState({ kind: 'active' })}>
                Add grouping
              </Button>
            ) : (
              <>
                {isDisaggregated && chartData.breakout ? (
                  <BasicAlert type='warning' className='mt-3'>
                    Grouping won&apos;t render while multiple {INITIATIVE.PLURAL} are selected — switch to a single {INITIATIVE.SINGULAR} to see this chart.
                  </BasicAlert>
                ) : null}
                <BreakoutConfig
                  metric={selectedMetric}
                  value={chartData.breakout}
                  onChange={(breakout) => {
                    setChartData((prev) => ({ ...prev, breakout }));
                    // selectedMetric gate above implies firstMetricCode is defined; check is defensive.
                    if (breakout === undefined && firstMetricCode !== undefined) {
                      setBreakoutState({ kind: 'cleared', metricCode: firstMetricCode });
                    }
                  }}
                />
              </>
            )
          ) : null}
        </CollapseContent>
      </CollapsePanel>
    </EditorTemplate>
  );
};
